// packages/console/src/api/live.ts
//
// Live-session telemetry client. One bearer-authenticated, workspace-scoped SSE
// stream (GET /v1/streams/live under the telemetry service) feeds every readout
// on the LIVE console. It mirrors api/sse.ts + api/telemetry.ts: connectSse owns
// the transport (token attach, one refresh-and-retry on 401, capped-backoff
// reconnect, sticky replay), and this module maps each event name to a typed
// payload and hands it to a handler.
//
// The honest-absence rule is load-bearing (R10): a stopped or partially-known
// stream OMITS the figures it does not have (startedAtMs, tcSeconds,
// ingestBitsPerSec, glassToGlassMs, egressBitsPerSec, frames…). An omitted key
// arrives as null here and the UI renders an em-dash for it, never a fabricated
// zero. The backend does not exist yet, so with no session the stream yields
// nothing and every panel sits in its stopped/em-dash state.

import { servicePaths } from "./config";
import { connectSse, type SseConnection, type SseStatus } from "./sse";

const base = `${servicePaths.telemetry}/v1/streams`;

export type StreamState = "waiting" | "on_air" | "paused" | "stopped" | "failed";
export type RungState = "encoding" | "idle" | "off";
export type DestHealth = "ok" | "warn" | "err" | "idle";

// LiveStream is the session-wide "stream" event: the one bonded ingest fanned
// out to a ladder. Every rate/latency/frame figure is nullable (absent, not
// zero) so a stopped or still-waiting session reads as em-dash, not 0.
export interface LiveStream {
  streamId: string;
  name: string;
  state: StreamState;
  // error is the setup-failure reason on a state === "failed" stream (undefined
  // on any other state), so the monitor can show WHY a broadcast never went live.
  error?: string;
  startedAtMs: number | null;
  tcSeconds: number | null;
  ingestBitsPerSec: number | null;
  ingestRttMs: number | null;
  glassToGlassMs: number | null;
  egressBitsPerSec: number | null;
  framesEncoded: number | null;
  framesDropped: number | null;
  // sourceFps is the DETECTED source frame rate (frames/sec) the ingest measured,
  // so the operator sees the cadence a "Match source" rung will inherit. Nullable
  // per the honest-absence rule (R10): absent until the source is probed, it maps
  // to null and renders as an em-dash rather than a fabricated 0.
  sourceFps: number | null;
  // Source stream properties detected off the decode banner (resolution, video
  // codec, audio codec). Nullable per the honest-absence rule: absent until the
  // decode reports them, and sourceAudioCodec stays null for a video-only source;
  // each renders as an em-dash rather than a fabricated value.
  sourceResolution: string | null;
  sourceVideoCodec: string | null;
  sourceAudioCodec: string | null;
}

// LiveLink is one bonded WAN link ("link" event). bitsPerSec/rttMs/lossPct are
// present per sample; up is the carrier's reachability.
export interface LiveLink {
  streamId: string;
  linkId: string;
  name: string;
  carrier: string | null;
  bitsPerSec: number | null;
  rttMs: number | null;
  lossPct: number | null;
  up: boolean;
}

// LiveRung is one ladder rung ("rung" event): its enable state, codec/profile,
// target and measured bitrate, GPU share, and encode state.
export interface LiveRung {
  streamId: string;
  rungName: string;
  on: boolean;
  codec: string;
  profile: string;
  targetBitsPerSec: number | null;
  measuredBitsPerSec: number | null;
  gpuSharePct: number | null;
  state: RungState;
}

// LiveDest is one push destination ("dest" event).
export interface LiveDest {
  streamId: string;
  destId: string;
  name: string;
  proto: string;
  host: string;
  rend: string;
  health: DestHealth;
}

// LiveHw is the encoder-hardware sample ("hw" event). Every figure is nullable
// (absent on a GPU-less host, per the same honest-absence rule as FT-4).
export interface LiveHw {
  streamId: string;
  gpuUtilPct: number | null;
  cpuUtilPct: number | null;
  vramPct: number | null;
  junctionC: number | null;
  powerW: number | null;
  sessions: number | null;
}

// LiveAggregate is the sticky fleet roll-up ("aggregate" event): how many live
// streams are currently active across the workspace.
export interface LiveAggregate {
  // Nullable per the honest-absence rule (R10): a partial roll-up before the
  // fleet count is known omits the field, which maps to null and renders as an
  // em-dash rather than a fabricated 0.
  activeStreams: number | null;
}

// LiveLog is one pipeline log line ("log" event).
export interface LiveLog {
  streamId: string;
  at: string;
  tag: string;
  line: string;
}

// LiveStatus is the sticky connection status ("status" event), replayed on
// every (re)connect so a fresh client learns the session state immediately.
export interface LiveStatus {
  stream: "live";
  state: StreamState;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function strOrNull(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function bool(v: unknown): boolean {
  return v === true;
}

const STREAM_STATES: StreamState[] = ["waiting", "on_air", "paused", "stopped", "failed"];
function streamState(v: unknown): StreamState {
  return STREAM_STATES.includes(v as StreamState) ? (v as StreamState) : "stopped";
}

const RUNG_STATES: RungState[] = ["encoding", "idle", "off"];
function rungState(v: unknown): RungState {
  return RUNG_STATES.includes(v as RungState) ? (v as RungState) : "off";
}

const DEST_HEALTHS: DestHealth[] = ["ok", "warn", "err", "idle"];
function destHealth(v: unknown): DestHealth {
  return DEST_HEALTHS.includes(v as DestHealth) ? (v as DestHealth) : "idle";
}

// readStream maps the raw "stream" payload, coercing every optional figure to
// null when omitted (honest absence). state falls back to "stopped".
function readStream(raw: Record<string, unknown>): LiveStream {
  const error = str(raw.error);
  return {
    streamId: str(raw.streamId),
    name: str(raw.name),
    state: streamState(raw.state),
    // Carry the failure reason only when present (omitempty on the wire), so a
    // healthy stream leaves error undefined rather than an empty string.
    ...(error ? { error } : {}),
    startedAtMs: num(raw.startedAtMs),
    tcSeconds: num(raw.tcSeconds),
    ingestBitsPerSec: num(raw.ingestBitsPerSec),
    ingestRttMs: num(raw.ingestRttMs),
    glassToGlassMs: num(raw.glassToGlassMs),
    egressBitsPerSec: num(raw.egressBitsPerSec),
    framesEncoded: num(raw.framesEncoded),
    framesDropped: num(raw.framesDropped),
    sourceFps: num(raw.sourceFps),
    sourceResolution: strOrNull(raw.sourceResolution),
    sourceVideoCodec: strOrNull(raw.sourceVideoCodec),
    sourceAudioCodec: strOrNull(raw.sourceAudioCodec),
  };
}

function readLink(raw: Record<string, unknown>): LiveLink {
  return {
    streamId: str(raw.streamId),
    linkId: str(raw.linkId),
    name: str(raw.name),
    carrier: strOrNull(raw.carrier),
    bitsPerSec: num(raw.bitsPerSec),
    rttMs: num(raw.rttMs),
    lossPct: num(raw.lossPct),
    up: bool(raw.up),
  };
}

function readRung(raw: Record<string, unknown>): LiveRung {
  return {
    streamId: str(raw.streamId),
    rungName: str(raw.rungName),
    on: bool(raw.on),
    codec: str(raw.codec),
    profile: str(raw.profile),
    targetBitsPerSec: num(raw.targetBitsPerSec),
    measuredBitsPerSec: num(raw.measuredBitsPerSec),
    gpuSharePct: num(raw.gpuSharePct),
    state: rungState(raw.state),
  };
}

function readDest(raw: Record<string, unknown>): LiveDest {
  return {
    streamId: str(raw.streamId),
    destId: str(raw.destId),
    name: str(raw.name),
    proto: str(raw.proto),
    host: str(raw.host),
    rend: str(raw.rend),
    health: destHealth(raw.health),
  };
}

function readHw(raw: Record<string, unknown>): LiveHw {
  return {
    streamId: str(raw.streamId),
    gpuUtilPct: num(raw.gpuUtilPct),
    cpuUtilPct: num(raw.cpuUtilPct),
    vramPct: num(raw.vramPct),
    junctionC: num(raw.junctionC),
    powerW: num(raw.powerW),
    sessions: num(raw.sessions),
  };
}

function readAggregate(raw: Record<string, unknown>): LiveAggregate {
  return { activeStreams: num(raw.activeStreams) };
}

function readLog(raw: Record<string, unknown>): LiveLog {
  return {
    streamId: str(raw.streamId),
    at: str(raw.at),
    tag: str(raw.tag),
    line: str(raw.line),
  };
}

function readStatus(raw: Record<string, unknown>): LiveStatus {
  return { stream: "live", state: streamState(raw.state) };
}

export interface LiveHandlers {
  onStream?: (s: LiveStream) => void;
  onLink?: (l: LiveLink) => void;
  onRung?: (r: LiveRung) => void;
  onDest?: (d: LiveDest) => void;
  onHw?: (h: LiveHw) => void;
  onAggregate?: (a: LiveAggregate) => void;
  onLog?: (l: LiveLog) => void;
  onStatus?: (s: LiveStatus) => void;
  onDropped?: (n: number) => void;
  onConnState?: (s: SseStatus) => void;
}

// streamLiveSession opens the workspace-scoped live SSE stream and dispatches
// each event to its typed handler. It returns the SseConnection so the caller
// closes it on unmount. The server replays sticky events (status, aggregate) on
// every reconnect, so a fresh connection learns the session state at once.
export function streamLiveSession(h: LiveHandlers): SseConnection {
  return connectSse(`${base}/live`, {
    onStatus: h.onConnState,
    onEvent: (ev) => {
      const data = safeParse(ev.data);
      if (!data) return;
      switch (ev.event) {
        case "stream":
          h.onStream?.(readStream(data));
          break;
        case "link":
          h.onLink?.(readLink(data));
          break;
        case "rung":
          h.onRung?.(readRung(data));
          break;
        case "dest":
          h.onDest?.(readDest(data));
          break;
        case "hw":
          h.onHw?.(readHw(data));
          break;
        case "aggregate":
          h.onAggregate?.(readAggregate(data));
          break;
        case "log":
          h.onLog?.(readLog(data));
          break;
        case "status":
          h.onStatus?.(readStatus(data));
          break;
        case "dropped":
          h.onDropped?.(num(data.dropped) ?? 0);
          break;
      }
    },
  });
}

function safeParse(s: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(s);
    return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}
